import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import TranslationConstant from '@/common/constant/TranslationConstant';
import localStorageUtil from '@/common/utils/localStorageUtil';
import { translateContentOnDemand } from '@/service/translationService';

const {
    ON_DEMAND_TRANSLATION_STATUS,
    ON_DEMAND_TRANSLATION_VIEW,
    ON_DEMAND_SOURCE_TYPE,
    ON_DEMAND_OUTLINE_KEY
} = TranslationConstant;

// postTypes whose text content can be translated on demand (spec §5.2).
// msg_files: only the text part; msg_voice: transcript (always translatable).
const TRANSLATABLE_POST_TYPES = [
    Constant.POST_TYPE.MSG,
    Constant.POST_TYPE.MSG_FILES,
    Constant.POST_TYPE.MSG_VOICE
];

/**
 * Whether a chat thread is the Customer thread of an email ticket (spec §5.4).
 *
 * Email Customer thread messages are rich text (like a ticket richtext field) and
 * their delivered/undelivered posts are all real emails shown in the thread.
 * Takes threadType (number) rather than the thread object so it works both for the
 * selected thread and for each thread record in 重ね表示 (stacked view).
 *
 * @param {Object} ticketInfo - The ticket detail.
 * @param {number} threadType - The thread's threadType (Constant.THREAD_TYPE).
 * @returns {boolean}
 */
export const isEmailCustomerThread = (ticketInfo, threadType) => {
    return (
        ticketInfo?.ticketType === Constant.TICKET_TYPE.EMAIL &&
        threadType === Constant.THREAD_TYPE.CUSTOMER
    );
};

/**
 * Decide whether a chat post is eligible for on-demand translation (spec §5.2/§5.3).
 *
 * Skips system events / image / file-only posts, empty content, and the viewer's
 * own messages (except msg_voice transcripts, which are always translatable since
 * they are the call content, not something the user typed).
 *
 * @param {Object} post - The chat post (ChatPost).
 * @param {Array<{id: string, threadType: number}>} threadChatList - Threads currently
 *   displayed, used to look up each post's threadType by post.chatThreadId.
 * @param {number} [fallbackThreadType] - threadType to use when the post's thread is
 *   not found in threadChatList (e.g. the post has no chatThreadId). Keeps the
 *   AI_ASSISTANT skip working instead of silently treating the post as translatable.
 * @returns {boolean}
 */
export const isTranslatableChatPost = (post, threadChatList = [], fallbackThreadType) => {
    if (!post || !TRANSLATABLE_POST_TYPES.includes(post.postType)) {
        return false;
    }

    // Resolve the post's threadType from the displayed threads; fall back to the
    // provided threadType when the post's thread is not in the list (missing/unmatched
    // chatThreadId) so the AI_ASSISTANT skip below is not lost.
    const matchedThreadType = threadChatList.find(t => t.id === post.chatThreadId)?.threadType;
    const postThreadType = matchedThreadType ?? fallbackThreadType;
    // Don't translate if threadType === AI_ASSISTANT
    if (postThreadType === Constant.THREAD_TYPE.AI_ASSISTANT) {
        return false;
    }
    // Nothing to translate when the text content is empty (e.g. file-only msg_files).
    if (Utils.isEmptyString(post.content)) {
        return false;
    }
    // The viewer's own text is already in their language — skip it. msg_voice is an
    // exception: the transcript is call content, not authored by the viewer.
    const viewerUserId = localStorageUtil.getUserDetailId();
    if (post.postType !== Constant.POST_TYPE.MSG_VOICE && post.creatorId === viewerUserId) {
        return false;
    }
    return true;
};

/**
 * Whether a message still needs translating: eligible, no translation cached yet,
 * and not already in flight (status !== LOADING). The LOADING guard is essential for
 * the auto-translate effect so setting LOADING (which mutates the atom) does not
 * re-trigger the effect and cause a request loop.
 *
 * @param {Object} msg - A message record from a thread's messages map.
 * @returns {boolean}
 */
const isPendingTranslationMessage = (msg) =>
    !!msg &&
    msg.isTranslatable &&
    msg.translated == null &&
    msg.status !== ON_DEMAND_TRANSLATION_STATUS.LOADING &&
    msg.status !== ON_DEMAND_TRANSLATION_STATUS.ERROR;

/**
 * Build the messages map for a thread record from the current post list, keeping
 * any translation already cached for a post (so re-init after new posts arrive or
 * the list refreshes does not drop existing translations).
 *
 * @param {Array} postList - Chat posts currently in the thread.
 * @param {Object} prevMessages - Existing messages map to merge from (may be undefined).
 * @param {string} threadView - The thread-level active view to apply to new messages.
 * @param {Array<{id: string, threadType: number}>} threadChatList - Threads currently
 *   displayed, used by isTranslatableChatPost to resolve each post's threadType.
 * @param {number} [fallbackThreadType] - threadType used when a post's thread is not
 *   found in threadChatList (passed through to isTranslatableChatPost).
 * @returns {Object} messages map keyed by postId.
 */
const buildMessagesMap = (
    postList,
    prevMessages = {},
    threadView = ON_DEMAND_TRANSLATION_VIEW.ORIGINAL,
    threadChatList = [],
    fallbackThreadType
) => {
    if (!Array.isArray(postList)) {
        return {};
    }

    return postList.reduce((acc, post) => {
        if (!post?.id) {
            return acc;
        }

        const prev = prevMessages[post.id];
        const translated = prev?.translated ?? null;
        const activeView = translated != null
            ? (prev?.activeView ?? threadView)
            : ON_DEMAND_TRANSLATION_VIEW.ORIGINAL;
        acc[post.id] = {
            postType: post.postType,
            creatorId: post.creatorId,
            original: post.content,
            isTranslatable: isTranslatableChatPost(post, threadChatList, fallbackThreadType),
            translated,
            status: prev?.status ?? ON_DEMAND_TRANSLATION_STATUS.IDLE,
            activeView
        };
        return acc;
    }, {});
};

/**
 * Initialize / refresh the on-demand translation state for one or more chat
 * threads, derived from the current visible post list.
 *
 * When 重ね表示 is active the postList contains messages from multiple threads.
 * This function groups posts by their chatThreadId and creates / merges a record
 * for each thread independently, so each message is stored under the correct
 * thread key (fixes messages of one thread leaking into another).
 *
 * Preserves the thread-level toggleStatus/activeView and any per-message cached
 * translations across refreshes; only the messages map is rebuilt from the latest
 * post list (new eligible posts are added with IDLE status).
 *
 * @param {Function} setChatPostOnDemand - Recoil setter for chatPostOnDemandAtom.
 * @param {string} threadId - The primary (selectThread) thread id. Ensures this
 *   thread's record exists even when the post list is empty, and is used as the
 *   fallback thread for posts without a chatThreadId.
 * @param {string} [ticketOutline] - The ticket/knowledge outline (title) as computed
 *   by the caller (generateTicketOutlineOnTabChat / generateKnowledgeOutlineOnTabChat).
 *   Stored on every thread record so it can be translated together with the thread. A
 *   change of value invalidates the cached translated title; an empty value (ticket not
 *   loaded yet) keeps what is already stored.
 * @param {string} [ticketId] - The ticket the threads belong to. Stored on every
 *   thread record so a CHAT TAB (whose tab.id is the ticketId, not a threadId) can
 *   find the translated title of its ticket. Empty keeps the stored value.
 * @param {Object} params - { postList, threadType }.
 */
export const initChatPostOnDemand = ({
    setChatPostOnDemand,
    chatThreadId,
    threadType,
    postList,
    ticketOutline = '',
    ticketId = '',
    threadChatList = []
}) => {
    if (typeof setChatPostOnDemand !== 'function' || !chatThreadId) {
        return;
    }

    // Group posts by their actual chatThreadId so 重ね表示 (stacked view) stores
    // each message under the correct thread record.
    const postsByThread = {};
    (postList || []).forEach((post) => {
        const tid = post?.chatThreadId || chatThreadId;
        if (!postsByThread[tid]) {
            postsByThread[tid] = [];
        }
        postsByThread[tid].push(post);
    });

    // Ensure the primary thread record exists even when its post list is empty.
    if (!postsByThread[chatThreadId]) {
        postsByThread[chatThreadId] = [];
    }

    setChatPostOnDemand((prev) => {
        const next = { ...prev };
        // Track whether the recompute actually changed anything. When it does not
        // (this effect re-runs on every render because chatPostByChatThreadIdList is a
        // freshly-filtered array each time, even with no new post), we return `prev`
        // unchanged so Recoil skips the update — otherwise returning a new object here
        // notifies subscribers, re-renders, rebuilds the list, and re-runs this effect
        // in a tight loop (seen in the trace: initChatPostOnDemand firing ~every 150ms
        // with an unchanged post count).
        let changed = false;

        Object.entries(postsByThread).forEach(([tid, posts]) => {
            const record = prev[tid];
            const toggleStatus = record?.toggleStatus || ON_DEMAND_TRANSLATION_STATUS.IDLE;
            const activeView = record?.activeView || ON_DEMAND_TRANSLATION_VIEW.ORIGINAL;
            const recordThreadType = tid === chatThreadId ? threadType : record?.threadType;

            // When this thread has no posts in the current list (e.g. a transient empty batch
            // while toggling 重ね表示 / switching thread), keep the existing messages so cached
            // translations are not wiped. Only rebuild the messages map when posts are present.
            const nextMessages = Utils.isEmptyArray(posts)
                ? (record?.messages || {})
                : buildMessagesMap(posts, record?.messages, activeView, threadChatList, recordThreadType);

            // The ticket may not be loaded yet when this runs (the caller computes the title
            // from ticketInfo, fetched asynchronously): an empty value keeps what is stored
            // instead of wiping the known ticket/title.
            const prevTicketOutline = record?.ticketOutline || '';
            const nextTicketOutline = Utils.isEmptyString(ticketOutline) ? prevTicketOutline : ticketOutline;
            // Drop the cached translation when the original title changed (e.g. the outline was
            // edited) so the next translation refreshes it instead of showing a stale title.
            const nextTranslatedTicketOutline = nextTicketOutline === prevTicketOutline
                ? (record?.translatedTicketOutline ?? null)
                : null;

            const nextRecord = {
                toggleStatus,
                activeView,
                // Only the primary thread knows its threadType here; keep the
                // existing value for the other stacked threads.
                threadType: tid === chatThreadId ? threadType : record?.threadType,
                ticketId: Utils.isEmptyString(ticketId) ? (record?.ticketId || '') : ticketId,
                ticketOutline: nextTicketOutline,
                translatedTicketOutline: nextTranslatedTicketOutline,
                messages: nextMessages
            };

            // Reuse the existing record object when nothing meaningful changed so the
            // atom identity is preserved for unchanged threads.
            if (record && isSameThreadRecord(record, nextRecord)) {
                next[tid] = record;
            } else {
                next[tid] = nextRecord;
                changed = true;
            }
        });

        // No thread record changed -> keep the previous atom value (no re-render).
        return changed ? next : prev;
    });
};

/**
 * Shallow-ish equality for a thread record, comparing only the fields
 * initChatPostOnDemand can produce. `messages` is compared per-postId on the
 * fields that affect rendering/translation, so an unchanged recompute (same posts,
 * same cached translations) is recognized as equal and the atom is left untouched.
 *
 * @param {Object} a - previous record.
 * @param {Object} b - freshly computed record.
 * @returns {boolean} true when the two records are equivalent.
 */
const isSameThreadRecord = (a, b) => {
    if (a === b) return true;
    if (!a || !b) return false;

    if (
        a.toggleStatus !== b.toggleStatus ||
        a.activeView !== b.activeView ||
        a.threadType !== b.threadType ||
        a.ticketId !== b.ticketId ||
        a.ticketOutline !== b.ticketOutline ||
        a.translatedTicketOutline !== b.translatedTicketOutline
    ) {
        return false;
    }

    const aMessages = a.messages || {};
    const bMessages = b.messages || {};
    if (aMessages === bMessages) return true;

    const aKeys = Object.keys(aMessages);
    const bKeys = Object.keys(bMessages);
    if (aKeys.length !== bKeys.length) return false;

    for (const key of aKeys) {
        const am = aMessages[key];
        const bm = bMessages[key];
        if (!bm) return false;
        if (
            am.original !== bm.original ||
            am.translated !== bm.translated ||
            am.status !== bm.status ||
            am.activeView !== bm.activeView ||
            am.isTranslatable !== bm.isTranslatable ||
            am.postType !== bm.postType ||
            am.creatorId !== bm.creatorId
        ) {
            return false;
        }
    }
    return true;
};

/**
 * Set the active view (bản gốc / bản dịch) of a record and sync every message's
 * activeView with it. Pure helper working on one thread record.
 *
 * @param {Object} record - A thread record from the atom.
 * @param {string} activeView - One of ON_DEMAND_TRANSLATION_VIEW values.
 * @returns {Object} the updated record.
 */
const applyActiveViewToRecord = (record, activeView) => {
    const nextMessages = record.messages
        ? Object.fromEntries(
              Object.entries(record.messages).map(([postId, msg]) => [
                  postId,
                  { ...msg, activeView }
              ])
          )
        : record.messages;

    return { ...record, activeView, messages: nextMessages };
};

/**
 * Switch the thread-level active view (bản gốc / bản dịch) AND update every
 * message's activeView in sync.
 *
 * When the toggle is turned OFF the thread view goes back to ORIGINAL (all
 * message-level overrides are reset too — spec §4.3 / BR-6). When turned ON
 * all messages switch to TRANSLATION so cached translations show immediately
 * (no re-translate — spec §4.3 / BR-7).
 *
 * In 重ね表示 (isOverlap = true) a single toggle applies to the whole stacked
 * cluster, so the view is applied to every thread that has a record in the atom
 * (these are exactly the threads currently displayed). Otherwise only the given
 * thread is changed.
 *
 * @param {Function} setChatPostOnDemand - Recoil setter for chatPostOnDemandAtom.
 * @param {string} threadId - The chat thread id.
 * @param {string} activeView - One of ON_DEMAND_TRANSLATION_VIEW values.
 * @param {boolean} [isOverlap] - Whether 重ね表示 (stacked view) is active.
 */
export const setListChatPostOnDemandActiveView = (setChatPostOnDemand, threadId, activeView, isOverlap = false) => {
    if (typeof setChatPostOnDemand !== 'function' || !threadId) return;

    setChatPostOnDemand((prev) => {
        // Overlap: apply to every displayed thread (all records). Otherwise: one thread.
        const targetThreadIds = isOverlap ? Object.keys(prev || {}) : [threadId];

        const next = { ...prev };
        targetThreadIds.forEach((tid) => {
            const record = prev[tid];
            if (!record) return;
            next[tid] = applyActiveViewToRecord(record, activeView);
        });
        return next;
    });
};

/**
 * Collect the ids of the threads to translate for a toggle-ON action.
 *
 * - Normal view (isOverlap = false): only the current thread.
 * - Stacked view 「重ね表示」(isOverlap = true): every thread in the atom that has
 *   at least one eligible-but-not-yet-translated message (spec BR-16 / Q2).
 *
 * @param {Object} chatPostOnDemand - The chatPostOnDemandAtom value.
 * @param {string} threadId - The current (primary) thread id.
 * @param {boolean} isOverlap - Whether 重ね表示 is active.
 * @returns {string[]} thread ids to translate.
 */
const getThreadIdsNeedTranslate = (chatPostOnDemand, threadId, isOverlap) => {
    if (!isOverlap) {
        return [threadId];
    }

    return Object.entries(chatPostOnDemand || {})
        .filter(([, record]) =>
            Object.values(record?.messages || {}).some(isPendingTranslationMessage)
        )
        .map(([tid]) => tid);
};

/**
 * Build the request payload's `threads` map: { [threadId]: [{ postId, type }, ...] }
 * holding only the eligible, not-yet-translated messages of each thread, together
 * with each message's content type so the BE can pick the mimeType.
 *
 * A message in an email Customer thread is rich text (like a ticket richtext field);
 * everything else is plain text. This is decided per thread via the thread record's
 * threadType (§5.4).
 *
 * @param {Object} chatPostOnDemand - The chatPostOnDemandAtom value.
 * @param {string[]} threadIds - Threads to include.
 * @param {Object} ticketInfo - The ticket detail (used to detect email tickets).
 * @returns {Object} { [threadId]: Array<{ postId, type }> } (threads with no pending msg omitted).
 */
const getListMsgTranslateByThread = (chatPostOnDemand, threadIds, ticketInfo) => {
    return threadIds.reduce((acc, tid) => {
        const threadRecord = chatPostOnDemand?.[tid];
        const messages = threadRecord?.messages || {};

        // Email Customer thread → message body is rich text; otherwise plain text.
        const messageType = isEmailCustomerThread(ticketInfo, threadRecord?.threadType)
            ? Constant.FORM_TYPE.FIELD_RICHTEXT
            : Constant.FORM_TYPE.FIELD_TEXT;

        const listMsgTranslate = Object.entries(messages)
            .filter(([, msg]) => isPendingTranslationMessage(msg))
            .map(([postId]) => ({ postId, type: messageType }));

        if (!Utils.isEmptyArray(listMsgTranslate)) {
            acc[tid] = listMsgTranslate;
        }
        return acc;
    }, {});
};

/**
 * Resolve the SINGLE ticket outline (title) to send for translation.
 *
 * A ticket has exactly ONE outline but many chat threads: initChatPostOnDemand stores
 * a copy on every thread record only so each header can render it without an extra
 * lookup. The request needs just one copy, so it is read from the thread that
 * triggered the toggle - in 重ね表示 every stacked thread belongs to the same ticket,
 * so any of them carries the same outline.
 *
 * Returns '' as soon as the title is already translated ANYWHERE for this ticket, so
 * autoTranslateNewMessages (which fires on every new message while the toggle is ON)
 * never pays to translate the same title again. The check spans every thread of the
 * ticket - not just this one - because the translated title is only stored on the
 * threads of the request that produced it: in 重ね表示, or when a thread is initialized
 * after the translation happened, the entry-point thread can still have an empty
 * translatedTicketOutline while the ticket's title is in fact already translated.
 *
 * @param {Object} chatPostOnDemand - The chatPostOnDemandAtom value.
 * @param {string} threadId - The thread that triggered the translation.
 * @returns {string} the outline to translate, or '' when there is nothing to do.
 */
const getTicketOutlineNeedTranslate = (chatPostOnDemand, threadId) => {
    const record = chatPostOnDemand?.[threadId];
    if (!record) {
        return '';
    }

    // Already translated for this thread -> nothing to send.
    if (!Utils.isEmptyString(record.translatedTicketOutline)) {
        return '';
    }

    // Already translated for another thread of the SAME ticket -> nothing to send
    // either (one ticket has one outline). Skipped when the ticket is unknown.
    const isTranslatedOnTicket =
        !Utils.isEmptyString(record.ticketId) &&
        Object.values(chatPostOnDemand).some(
            (threadRecord) =>
                threadRecord?.ticketId === record.ticketId &&
                !Utils.isEmptyString(threadRecord?.translatedTicketOutline)
        );
    if (isTranslatedOnTicket) {
        return '';
    }

    return record.ticketOutline || '';
};

/**
 * Set the thread toggleStatus to LOADING AND mark every currently-pending message of
 * those threads as LOADING in one atom update.
 *
 * This is the actual fix for the request loop: autoTranslateNewMessages fires on every
 * atom change while the thread view is TRANSLATION, and it decides a message is still
 * "pending" from the MESSAGE-level status (isPendingTranslationMessage checks
 * msg.status !== LOADING). Previously the request only set the THREAD-level toggleStatus
 * to LOADING and left each message at IDLE, so the effect kept seeing the same message as
 * pending and re-fired the request dozens of times until the response came back. Marking
 * the messages LOADING takes them out of the pending set immediately, so only one request
 * is sent per new message.
 *
 * @param {Function} setChatPostOnDemand - Recoil setter.
 * @param {string[]} threadIds - Threads whose pending messages are being sent.
 */
const markThreadsAndMessagesLoading = (setChatPostOnDemand, threadIds) => {
    setChatPostOnDemand((prev) => {
        const next = { ...prev };
        threadIds.forEach((tid) => {
            const record = prev[tid];
            if (!record) return;

            const nextMessages = Object.fromEntries(
                Object.entries(record.messages || {}).map(([postId, msg]) => {
                    // Only the messages that will actually be sent (currently pending)
                    // become LOADING; already-translated / errored ones are untouched.
                    if (isPendingTranslationMessage(msg)) {
                        return [postId, { ...msg, status: ON_DEMAND_TRANSLATION_STATUS.LOADING }];
                    }
                    return [postId, msg];
                })
            );

            next[tid] = {
                ...record,
                toggleStatus: ON_DEMAND_TRANSLATION_STATUS.LOADING,
                messages: nextMessages
            };
        });
        return next;
    });
};

/**
 * Apply one thread-level status/view update to a set of threads in the atom.
 *
 * @param {Function} setChatPostOnDemand - Recoil setter.
 * @param {string[]} threadIds - Threads to update.
 * @param {Object} patch - Partial thread record ({ toggleStatus, activeView }).
 */
const updateThreadsToggleState = (setChatPostOnDemand, threadIds, patch) => {
    setChatPostOnDemand((prev) => {
        const next = { ...prev };
        threadIds.forEach((tid) => {
            if (next[tid]) {
                next[tid] = { ...next[tid], ...patch };
            }
        });
        return next;
    });
};

/**
 * Trigger the on-demand translation for the eligible, not-yet-translated messages
 * of a chat thread (or of every stacked thread when 重ね表示 is on).
 *
 * Flow (spec §4.1 / AC-2, BR-16):
 *   1. Resolve the thread(s) to translate and build the { [threadId]: [{ postId, type }] } payload.
 *   2. Set those threads to LOADING (toggle disabled while translating).
 *   3. Call the Translation Service (sourceType = CHAT). The BE fetches each post's
 *      content itself, translates it, and returns a { [postId]: translatedText } map
 *      (offloaded to S3). null => failure.
 *   4. Merge the translations back per message and flip the translated threads to
 *      TRANSLATED + activeView = TRANSLATION. On failure keep originals (ERROR).
 *
 * @param {Object} params
 * @param {string} params.threadId - The current (primary) thread id.
 * @param {Object} params.chatPostOnDemand - The current atom value.
 * @param {Function} params.setChatPostOnDemand - Recoil setter for chatPostOnDemandAtom.
 * @param {boolean} [params.isOverlap] - Whether 重ね表示 (stacked view) is active.
 */
export const requestChatPostOnDemandTranslation = async ({
    threadId,
    ticketInfo,
    chatPostOnDemand,
    setChatPostOnDemand,
    isOverlap = false,
    setIsOpenModalReloadPage
}) => {
    if (typeof setChatPostOnDemand !== 'function' || !threadId) return;

    const threadIds = getThreadIdsNeedTranslate(chatPostOnDemand, threadId, isOverlap);
    const threads = getListMsgTranslateByThread(chatPostOnDemand, threadIds, ticketInfo);
    const outline = getTicketOutlineNeedTranslate(chatPostOnDemand, threadId);

    const hasPendingMessages = !Utils.isEmptyObject(threads);
    // Threads the toggle applies to: in 重ね表示 the whole stacked cluster, i.e. the threads of
    // the SAME ticket (the atom is global and also holds the threads of other tickets / panels),
    // otherwise just this thread.
    const currentTicketId = chatPostOnDemand?.[threadId]?.ticketId;
    const toggledThreadIds = isOverlap
        ? Object.keys(chatPostOnDemand || {}).filter(
              (tid) =>
                  Utils.isEmptyString(currentTicketId) ||
                  chatPostOnDemand[tid]?.ticketId === currentTicketId
          )
        : [threadId];

    // Nothing left to translate at all - every eligible message is already translated (or
    // the thread has none) AND the title is done too. Still flip the view ON so cached
    // translations show and the switch reflects the ON state (spec §4.3 / BR-7).
    if (!hasPendingMessages && Utils.isEmptyString(outline)) {
        setListChatPostOnDemandActiveView(
            setChatPostOnDemand,
            threadId,
            ON_DEMAND_TRANSLATION_VIEW.TRANSLATION,
            isOverlap
        );
        updateThreadsToggleState(setChatPostOnDemand, toggledThreadIds, {
            toggleStatus: ON_DEMAND_TRANSLATION_STATUS.TRANSLATED
        });
        return;
    }

    // Threads that actually have pending messages get the loading indicator. When only the
    // title is missing its translation (messages all done / none eligible, e.g. a thread
    // holding just the viewer's own messages, or a title that became known only after the
    // messages were translated) the toggled thread(s) carry the request instead - otherwise
    // the title would never be sent and the header would stay untranslated while ON.
    const loadingThreadIds = hasPendingMessages ? Object.keys(threads) : toggledThreadIds;
    // Set BOTH the thread toggleStatus AND each pending message's status to LOADING.
    // Marking messages (not just the thread) is what stops autoTranslateNewMessages from
    // re-firing the same request in a loop (see markThreadsAndMessagesLoading).
    markThreadsAndMessagesLoading(setChatPostOnDemand, loadingThreadIds);

    const affiliationOrganization = localStorageUtil.getAffiliationOrganization();
    const userId = localStorageUtil.getUserDetailId();

    // Send the ids + content type ({ [threadId]: [{ postId, type }] }); the BE fetches
    // each post's content itself and returns a flat { [postId]: translatedText } map (S3).
    // The ticket title is FE-computed (the BE can't rebuild it) so it is sent as plain
    // text under `outline` - ONE copy per request, like the ticket detail does.
    // threadId travels along as the entry point: the BE keys its result file by thread and
    // `threads` can be empty on a title-only request.
    const translateArgs = {
        sourceType: ON_DEMAND_SOURCE_TYPE.CHAT,
        threadId,
        threads,
        outline
    };

    const { translations: translationsByPostId, errorCode } = await translateContentOnDemand(
        affiliationOrganization,
        userId,
        translateArgs
    );

    // Handle invalid translate by errorCode
    if (errorCode) return setIsOpenModalReloadPage(true);

    // null => the service failed (permission / network / S3 / fetch): keep originals, mark ERROR.
    if (translationsByPostId == null) {
        // Set the thread ERROR AND move the in-flight (LOADING) messages to ERROR. Leaving
        // them LOADING would keep them out of the pending set (no retry) forever; leaving
        // them IDLE would put them straight back into the pending set and loop the request.
        // ERROR parks them until a new attempt is triggered explicitly.
        setChatPostOnDemand((prev) => {
            const next = { ...prev };
            loadingThreadIds.forEach((tid) => {
                const record = prev[tid];
                if (!record) return;

                const nextMessages = Object.fromEntries(
                    Object.entries(record.messages || {}).map(([postId, msg]) => {
                        if (msg?.status === ON_DEMAND_TRANSLATION_STATUS.LOADING) {
                            return [postId, { ...msg, status: ON_DEMAND_TRANSLATION_STATUS.ERROR }];
                        }
                        return [postId, msg];
                    })
                );

                next[tid] = {
                    ...record,
                    toggleStatus: ON_DEMAND_TRANSLATION_STATUS.ERROR,
                    messages: nextMessages
                };
            });
            return next;
        });
        return;
    }

    // The outline sits under a reserved key (not a postId), so pull it out before
    // merging the message translations.
    const translatedOutline = translationsByPostId[ON_DEMAND_OUTLINE_KEY];

    // Merge translated content per message and flip the translated threads ON.
    setChatPostOnDemand((prev) => {
        const next = { ...prev };

        // ONE outline per ticket, but each thread record keeps its own copy so its
        // header can render it: spread the translated title to EVERY thread of the same
        // ticket, not just the threads of this request. Without this, a thread left out
        // of the request (重ね表示, or initialized after the translation) would keep
        // showing the original title - and getTicketOutlineNeedTranslate would never
        // resend it. Scoped by ticketId because the atom is global and also holds the
        // threads of other tickets / panels.
        const hasTranslatedOutline = !Utils.isEmptyString(translatedOutline);
        const requestTicketId = prev[threadId]?.ticketId;
        if (hasTranslatedOutline && !Utils.isEmptyString(requestTicketId)) {
            Object.entries(prev).forEach(([tid, record]) => {
                if (record?.ticketId !== requestTicketId) return;
                next[tid] = { ...record, translatedTicketOutline: translatedOutline };
            });
        }

        loadingThreadIds.forEach((tid) => {
            // Read back from `next` so the outline just spread above is not overwritten.
            const record = next[tid] || prev[tid];
            if (!record) return;

            const nextMessages = Object.fromEntries(
                Object.entries(record.messages || {}).map(([postId, msg]) => {
                    const translated = translationsByPostId[postId];
                    // A message that got a translation is now viewable as TRANSLATION;
                    // one that failed/was skipped keeps its original (no toggle link).
                    if (translated != null) {
                        return [postId, {
                            ...msg,
                            translated,
                            status: ON_DEMAND_TRANSLATION_STATUS.TRANSLATED,
                            activeView: ON_DEMAND_TRANSLATION_VIEW.TRANSLATION
                        }];
                    }
                    // Already translated in an earlier request: the thread is going ON, so show
                    // its cached translation too (spec §4.3 / BR-7) instead of leaving it on the
                    // original - this response simply carried nothing new for it.
                    if (msg?.translated != null) {
                        return [postId, { ...msg, activeView: ON_DEMAND_TRANSLATION_VIEW.TRANSLATION }];
                    }
                    // A message we set to LOADING for this request but the response carried
                    // no translation for it: release it back to IDLE so it is not stuck
                    // LOADING forever (which would keep it out of the pending set and never
                    // be retried).
                    if (msg?.status === ON_DEMAND_TRANSLATION_STATUS.LOADING) {
                        return [postId, { ...msg, status: ON_DEMAND_TRANSLATION_STATUS.IDLE }];
                    }
                    return [postId, msg];
                })
            );

            next[tid] = {
                ...record,
                toggleStatus: ON_DEMAND_TRANSLATION_STATUS.TRANSLATED,
                activeView: ON_DEMAND_TRANSLATION_VIEW.TRANSLATION,
                // Keep the cached title when this response carried no outline (e.g. the
                // auto-translate of newly arrived messages does not resend it).
                translatedTicketOutline: hasTranslatedOutline
                    ? translatedOutline
                    : (record.translatedTicketOutline ?? null),
                messages: nextMessages
            };
        });

        return next;
    });
};

/**
 * Auto-translate messages that arrived while a thread is already ON (spec §4.4).
 *
 * A thread is "ON" when its toggleStatus is TRANSLATED. When a new message arrives
 * it is merged into the atom untranslated (translated == null, shown as ORIGINAL).
 * This finds every ON thread that has such a pending message and triggers a
 * translation for it (per thread, so it works the same in normal and 重ね表示 view).
 *
 * A thread currently translating (toggleStatus === LOADING) is skipped, and
 * isPendingTranslationMessage filters messages already in flight — together these
 * prevent this effect (driven by the atom) from re-triggering into a request loop.
 *
 * @param {Object} chatPostOnDemand - The current atom value.
 * @param {Function} setChatPostOnDemand - Recoil setter for chatPostOnDemandAtom.
 * @param {Object} ticketInfo - The ticket detail (for email richtext detection).
 */
export const autoTranslateNewMessages = ({chatPostOnDemand, setChatPostOnDemand, ticketInfo, isOverlap, setIsOpenModalReloadPage}) => {
    if (typeof setChatPostOnDemand !== 'function' || !chatPostOnDemand) return;

    // Threads whose toggle is ON (currently showing the translation) and that have
    // at least one message still pending. "ON" is determined by activeView, not
    // toggleStatus: turning the toggle OFF only sets activeView back to ORIGINAL and
    // keeps toggleStatus = TRANSLATED (so cached translations survive). Using
    // toggleStatus here would wrongly auto-translate new messages after the user
    // turned the toggle OFF.
    const onThreadIdsWithPending = Object.entries(chatPostOnDemand)
        .filter(([, record]) =>
            record?.activeView === ON_DEMAND_TRANSLATION_VIEW.TRANSLATION &&
            Object.values(record.messages || {}).some(isPendingTranslationMessage)
        )
        .map(([tid]) => tid);

    if (Utils.isEmptyArray(onThreadIdsWithPending)) return;

    // 重ね表示: requestChatPostOnDemandTranslation itself gathers every pending thread
    // (isOverlap = true), so call it ONCE — passing any pending thread as the entry
    // point — to avoid duplicate requests for the same cluster. Otherwise translate
    // the single displayed thread.
    if (isOverlap) {
        requestChatPostOnDemandTranslation({
            threadId: onThreadIdsWithPending[0],
            ticketInfo,
            chatPostOnDemand,
            setChatPostOnDemand,
            isOverlap: true,
            setIsOpenModalReloadPage,
        });
        return;
    }

    onThreadIdsWithPending.forEach((tid) => {
        requestChatPostOnDemandTranslation({
            threadId: tid,
            ticketInfo,
            chatPostOnDemand,
            setChatPostOnDemand,
            isOverlap: false,
            setIsOpenModalReloadPage,
        });
    });
};

/**
 * Toggle a single message between its original and translated view (spec §4.2),
 * independent of the other messages. No-op if the record/message is absent.
 *
 * @param {Function} setChatPostOnDemand - Recoil setter for chatPostOnDemandAtom.
 * @param {string} threadId - The chat thread id.
 * @param {string} postId - The chat post id.
 * @param {string} activeView - One of ON_DEMAND_TRANSLATION_VIEW values.
 */
export const setMessageActiveView = (setChatPostOnDemand, threadId, postId, activeView) => {
    if (typeof setChatPostOnDemand !== 'function' || !threadId || !postId) return;

    setChatPostOnDemand((prev) => {
        const record = prev[threadId];
        const message = record?.messages?.[postId];
        if (!record || !message) return prev;

        return {
            ...prev,
            [threadId]: {
                ...record,
                messages: {
                    ...record.messages,
                    [postId]: { ...message, activeView }
                }
            }
        };
    });
};

/**
 * Resolve the message record for a post from the atom state.
 *
 * @param {Object} chatPostOnDemand - The chatPostOnDemandAtom value.
 * @param {string} threadId - The chat thread id.
 * @param {string} postId - The chat post id.
 * @returns {Object|null} the message record or null.
 */
export const getChatPostMessageRecord = (chatPostOnDemand, threadId, postId) => {
    if (!chatPostOnDemand || !threadId || !postId) return null;
    return chatPostOnDemand[threadId]?.messages?.[postId] || null;
};

/**
 * Resolve the thread-level record for a chat thread from the atom state.
 *
 * Exposes the thread's shared 「翻訳版」toggle view (activeView): ORIGINAL when the
 * toggle is OFF, TRANSLATION when ON. A message's per-message switch link must only
 * show while the thread toggle is ON (spec §4.3 / BR-5 / AC-5).
 *
 * @param {Object} chatPostOnDemand - The chatPostOnDemandAtom value.
 * @param {string} threadId - The chat thread id.
 * @returns {Object|null} the thread record or null.
 */
export const getChatPostThreadRecord = (chatPostOnDemand, threadId) => {
    if (!chatPostOnDemand || !threadId) return null;
    return chatPostOnDemand[threadId] || null;
};

// The per-message switch link only shows while the shared 「翻訳版」thread toggle is ON
// (thread record activeView === TRANSLATION). When the toggle is OFF the link disappears
// and the message shows its original (spec §4.3 point 4 / BR-5 / AC-5).
export const isThreadToggleOn = (threadRecord) =>
    threadRecord?.activeView === ON_DEMAND_TRANSLATION_VIEW.TRANSLATION;

/**
 * Whether any of the given threads still has a translation in flight or about to start:
 * a message currently LOADING, or (thread toggle ON) a translatable message without a
 * translation that auto-translate will pick up. ERROR messages are never pending.
 * Used to delay focus-scroll until translated content has replaced the original.
 *
 * @param {Object} chatPostOnDemand - The chatPostOnDemandAtom value.
 * @param {string[]} threadIds - Threads displayed in the current chat panel.
 * @returns {boolean}
 */
export const hasPendingTranslation = (chatPostOnDemand, threadIds = []) => {
    if (!chatPostOnDemand || Utils.isEmptyArray(threadIds)) return false;

    return threadIds.some((tid) => {
        const record = chatPostOnDemand[tid];
        if (!record) return false;
        const isToggleOn = isThreadToggleOn(record);
        return Object.values(record.messages || {}).some((msg) =>
            msg?.status === ON_DEMAND_TRANSLATION_STATUS.LOADING ||
            (isToggleOn && isPendingTranslationMessage(msg))
        );
    });
};

/**
 * Resolve the on-demand record a CHAT TAB should use for its title, looked up by
 * ticketId (a chat tab's tab.id is the ticketId, while the atom is keyed by threadId).
 *
 * A ticket has ONE outline shared by all its threads, so the first thread of that
 * ticket currently showing the translation decides the tab title. Returns null when
 * no thread of the ticket has its 「翻訳版」toggle ON (or nothing translated yet), so
 * the caller keeps the original tab title.
 *
 * The result is normalized to the same shape as a content-detail record
 * ({ activeView, ticketOutline, translatedOutline }) so the shared tab-title
 * resolver works for both features unchanged.
 *
 * @param {Object} chatPostOnDemand - The chatPostOnDemandAtom value.
 * @param {string} ticketId - The ticket id (chat tab id).
 * @returns {Object|null} normalized record, or null to keep the original title.
 */
export const getChatOnDemandRecordByTicketId = (chatPostOnDemand, ticketId) => {
    if (!chatPostOnDemand || Utils.isEmptyString(ticketId)) return null;

    const record = Object.values(chatPostOnDemand).find(
        (threadRecord) =>
            threadRecord?.ticketId === ticketId &&
            threadRecord?.activeView === ON_DEMAND_TRANSLATION_VIEW.TRANSLATION &&
            !Utils.isEmptyString(threadRecord?.translatedTicketOutline)
    );
    if (!record) return null;

    return {
        activeView: record.activeView,
        ticketOutline: record.ticketOutline,
        translatedOutline: record.translatedTicketOutline
    };
};
